
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCompanyInvitations, resendInvitation, deleteInvitation } from '@/services/invitationService';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatEmailForDisplay } from '@/utils/emailUtils';
import { formatDateYMD } from '@/utils/dateFormat';
import { useToast } from '@/components/ui/use-toast';
import { RefreshCw, Trash2 } from 'lucide-react';
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog';

interface InvitationsListProps {
  companyId: string;
}

interface CompanyInvitation {
  id: string;
  company_id: string;
  email: string | { type: string; email: string };
  role: string;
  created_at: string;
  status: string;
  notes: string;
  updated_at: string;
  updated_by: string;
  user_id: string | null;
  invite_token: string;
  expires_at: string | null;
}

export const InvitationsList: React.FC<InvitationsListProps> = ({ companyId }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: invitations, isLoading } = useQuery({
    queryKey: ['company-invitations', companyId],
    queryFn: () => getCompanyInvitations(companyId)
  });

  const resendMutation = useMutation({
    mutationFn: (invitationId: string) => resendInvitation(invitationId),
    onSuccess: (result) => {
      if (result.emailStatus === 'sent') {
        toast({ title: "Invitation resent", description: "The invitation email has been resent successfully." });
      } else {
        toast({ title: "Invitation updated", description: result.emailErrorMessage || "The invitation was updated but the email failed to send.", variant: "destructive" });
      }
      queryClient.invalidateQueries({ queryKey: ['company-invitations', companyId] });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to resend invitation.", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (invitationId: string) => deleteInvitation(invitationId),
    onSuccess: () => {
      toast({ title: "Invitation deleted", description: "The invitation has been removed." });
      queryClient.invalidateQueries({ queryKey: ['company-invitations', companyId] });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to delete invitation.", variant: "destructive" });
    },
  });

  if (isLoading) {
    return <div>Loading invitations...</div>;
  }

  if (!invitations?.length) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pending Invitations</CardTitle>
        <CardDescription>List of people invited to join the company</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {invitations.map((invitation: CompanyInvitation) => (
            <div
              key={invitation.id}
              className="flex items-center justify-between p-4 border rounded-lg"
            >
              <div>
                <div className="font-medium">{formatEmailForDisplay(invitation.email)}</div>
                <div className="text-sm text-muted-foreground">
                  Invited {formatDateYMD(invitation.created_at)}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge>{invitation.role}</Badge>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => resendMutation.mutate(invitation.id)}
                  disabled={resendMutation.isPending}
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${resendMutation.isPending ? 'animate-spin' : ''}`} />
                  Resend
                </Button>
                <DeleteConfirmationDialog
                  entityName={`invitation for ${formatEmailForDisplay(invitation.email)}`}
                  onConfirm={() => deleteMutation.mutate(invitation.id)}
                  trigger={
                    <Button variant="outline" size="sm" disabled={deleteMutation.isPending}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  }
                />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
